import { bound } from "@frui.ts/helpers/dist";
import { Router } from "@frui.ts/screens/dist";
import { Modules } from "config";
import User, { UserValidationRules } from "entities/user";
import { action } from "mobx";
import UserRepository, * as userRepository from "repositories/userRepository";
import ConfirmationService from "services/confirmationService";
import EventBus, { invitationExtended } from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import AppRouter from "services/router";
import ListViewModel from "viewModels/listViewModel";
import SecurityService from "../../../services/securityService";
import UserCreateViewModel from "./userCreateViewModel";
import UserDetailViewModel from "./userDetailViewModel";
import UserRolesViewModel from "./userRolesViewModel";
import { attachAutomaticValidator, hideValidationErrors } from "@frui.ts/validation";

const navigationName = "manage-users";
@Router.registerRoute({
  route: navigationName,
  name: Router.Self,
  children: [UserDetailViewModel, UserCreateViewModel],
})
export default class UsersViewModel extends ListViewModel<User, UserDetailViewModel, UserCreateViewModel> {
  readonly modelName = "user";

  constructor(
    protected createViewModelFactory: () => UserCreateViewModel,
    protected detailViewModelFactory: ReturnType<typeof UserDetailViewModel.Factory>,
    protected repository: UserRepository,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected confirmationService: ConfirmationService,
    protected notificationService: NotificationService,
    protected securityService: SecurityService,
    private appRouter: AppRouter
  ) {
    super();
    this.navigationName = navigationName;
    attachAutomaticValidator(this.filter, UserValidationRules);
  }

  onInitialize() {
    this.unsubscriptions.push(this.eventBus.subscribe(invitationExtended, this.onInvitationExtended));
    return super.onInitialize();
  }

  protected fetchData() {
    const { limit, offset } = this.pagingFilter;
    const filter: User = this.appliedFilter as User;
    if (filter.searchText) {
      filter.searchText = filter.searchText.trim();
      this.filter.searchText = this.filter.searchText.trim();
    }
    return this.repository.getAll({ offset, limit }, filter);
  }

  protected resetFilterValues(filter: any): void {
    filter.type = "";
  }

  @bound
  onInvitationExtended(event: ReturnType<typeof invitationExtended>) {
    const user = this.items.find(item => item.id === event.payload.user.id);
    if (user) {
      user.invitation = event.payload.invitation;
    }
  }

  @action.bound
  resetFilterAndLoad(providerKind?: userRepository.ProviderKind) {
    this.filter = new User();
    this.filter.providerKind = providerKind;
    attachAutomaticValidator(this.filter, UserValidationRules);
    this.applyFilterAndLoad();
    hideValidationErrors(this.filter);
  }

  @action.bound
  selectProvider(providerKind?: userRepository.ProviderKind) {
    this.filter.providerKind = providerKind;
    this.navigationParamsValue = {
      ...this.navigationParams,
      providerKind: providerKind ?? "ALL",
    };
    this.notifyNavigationChanged();
    this.applyFilterAndLoad();
  }

  navigate(subPath: string | undefined, params: any): Promise<void> {
    if (params?.providerKind) {
      this.filter.providerKind = params.providerKind !== "ALL" ? params.providerKind : undefined;
      this.navigationParamsValue = {
        ...this.navigationParams,
        providerKind: params.providerKind,
      };
      this.applyFilter();
    }

    return super.navigate(subPath, params);
  }

  @bound
  onRoleClick(item: User) {
    this.appRouter.navigate(UserRolesViewModel, { userId: item.id });
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.users, `users.${code}`, size);
  }
}
